import React, { useCallback, useEffect, useState } from "react";
import api, { getUser } from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import useProductionUnit from "@/hooks/useProductionUnit";
import { Search, Package, AlertTriangle, XCircle, ScrollText, ShieldCheck, CheckCircle2, History, Sticker } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/datetime";

const TABS = [
  { key: "received", label: "Ready for QC", tone: "emerald", tag: "Finished Item", icon: Package, hint: "Post-plating stock ready for Quality Control" },
  { key: "damaged",  label: "Damaged",       tone: "amber",   tag: "Damaged",       icon: AlertTriangle, hint: "Damaged during plating — audit only" },
  { key: "lost",     label: "Lost",          tone: "red",     tag: "Lost",          icon: XCircle,       hint: "Lost during plating — audit only" },
  { key: "history",  label: "QC History",    tone: "indigo",  tag: "Log",           icon: History,       hint: "Every completed QC event" },
];

const TONE = {
  emerald: { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", text: "text-emerald-700", num: "text-emerald-800", ring: "border-emerald-300" },
  amber:   { pill: "bg-amber-50 text-amber-700 border-amber-200",       text: "text-amber-700",   num: "text-amber-800",   ring: "border-amber-300" },
  red:     { pill: "bg-red-50 text-red-700 border-red-200",             text: "text-red-700",     num: "text-red-800",     ring: "border-red-300" },
  indigo:  { pill: "bg-indigo-50 text-indigo-700 border-indigo-200",    text: "text-indigo-700",  num: "text-indigo-800",  ring: "border-indigo-300" },
};

export default function QualityControl() {
  const [tab, setTab] = useState("received");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [productionUnitId, setProductionUnitId] = useState("");
  const [categories, setCategories] = useState([]);
  const [unitOpts, setUnitOpts] = useState([]);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ received: 0, damaged: 0, lost: 0 });
  const [loading, setLoading] = useState(false);
  const [openLedgerFor, setOpenLedgerFor] = useState(null);
  const [qcFor, setQcFor] = useState(null);
  const [qcHistory, setQcHistory] = useState([]);
  const [qcHistoryTotal, setQcHistoryTotal] = useState(0);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/production/units").then((r) => setUnitOpts((r.data || []).filter((u) => u.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "history") {
        const params = { page: 1, page_size: 200 };
        if (search) params.search = search;
        if (categoryId) params.category = categoryId;
        if (productionUnitId) params.production_unit_id = productionUnitId;
        const r = await api.get("/production/qc-events", { params });
        setQcHistory(r.data.items || []);
        setQcHistoryTotal(r.data.total || 0);
        // still fetch totals for tile counts
        const t = await api.get("/production/finished-stocks", { params: { tab: "received" } });
        setTotals(t.data.totals || { received: 0, damaged: 0, lost: 0 });
      } else {
        const params = { tab };
        if (search) params.search = search;
        if (categoryId) params.category = categoryId;
        const r = await api.get("/production/finished-stocks", { params });
        setItems(r.data.items || []);
        setTotals(r.data.totals || { received: 0, damaged: 0, lost: 0 });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [tab, search, categoryId, productionUnitId]);
  useEffect(() => { load(); }, [load]);

  const activeTab = TABS.find((t) => t.key === tab);
  const tone = TONE[activeTab.tone];

  return (
    <div className="space-y-4">
      <PageSection title="Quality Control" description="Post-plating finished stock, damaged / lost audit log, and full QC history in one place">
        <div className="grid grid-cols-4 gap-3 mb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            const T = TONE[t.tone];
            const active = t.key === tab;
            const counter = t.key === "history" ? qcHistoryTotal : (totals[t.key] || 0);
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`qc-tab-${t.key}`}
                className={`text-left border rounded-lg p-3 transition ${active ? `${T.ring} shadow-sm` : "border-gray-200 hover:border-gray-300"}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${T.text}`} />
                  <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{t.label}</div>
                </div>
                <div className={`mt-1 text-2xl font-heading font-bold tabular-nums ${T.num}`}>
                  {(counter || 0).toLocaleString("en-IN")}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{t.hint}</div>
              </button>
            );
          })}
        </div>

        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "history" ? "Search QC # / RM / product / person..." : "Search raw material..."} className="pl-8" data-testid="qc-search" />
            </div>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="qc-category-filter">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            {unitOpts.length > 1 && tab === "history" && (
              <select value={productionUnitId} onChange={(e) => setProductionUnitId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="qc-unit-filter">
                <option value="">All units</option>
                {unitOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            {(categoryId || productionUnitId || search) && (
              <button onClick={() => { setSearch(""); setCategoryId(""); setProductionUnitId(""); }} className="text-[11px] text-gray-500 hover:text-gray-900 underline" data-testid="qc-clear-filters">Clear</button>
            )}
            <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${tone.pill}`}>{activeTab.tag}</span>
            <span className="text-xs text-gray-500 ml-auto">{tab === "history" ? `${qcHistory.length} events` : `${items.length} rows`}</span>
          </div>

          {tab !== "history" ? (
            <FinishedTable tab={tab} items={items} loading={loading} tone={tone} onLedger={setOpenLedgerFor} onQC={setQcFor} />
          ) : (
            <QCHistoryTable rows={qcHistory} loading={loading} />
          )}
        </Surface>
      </PageSection>

      {openLedgerFor && (
        <LedgerDialog rm={openLedgerFor} status={tab} onClose={() => setOpenLedgerFor(null)} />
      )}
      {qcFor && (
        <InitiateQCDialog row={qcFor} onClose={() => setQcFor(null)} onDone={() => { setQcFor(null); load(); }} />
      )}
    </div>
  );
}

function FinishedTable({ tab, items, loading, tone, onLedger, onQC }) {
  const activeTab = TABS.find((t) => t.key === tab);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
          <th className="px-3 py-2">Raw Material (RG-based)</th>
          <th className="px-3 py-2 text-center w-20">Unit</th>
          <th className="px-3 py-2 text-right w-32">{tab === "received" ? "Available Qty" : `${activeTab.label} Qty`}</th>
          <th className="px-3 py-2 text-right w-32">Movements</th>
          <th className="px-3 py-2 w-40">Last Movement</th>
          <th className="px-3 py-2 w-56 text-right"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {loading && (<tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>)}
        {!loading && items.length === 0 && (
          <tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
            No {activeTab.label.toLowerCase()} stock yet.
          </td></tr>
        )}
        {items.map((r) => (
          <tr key={r.raw_material_id} className="hover:bg-gray-50" data-testid={`qc-row-${r.raw_material_id}`}>
            <td className="px-3 py-2">
              <div className="font-medium">{r.raw_material_name}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{r.tag}</div>
            </td>
            <td className="px-3 py-2 text-center text-xs text-gray-600">{r.unit || "—"}</td>
            <td className={`px-3 py-2 text-right tabular-nums font-semibold ${tone.num}`}>{r.qty}</td>
            <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-600">{r.movement_count}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{formatDateTime(r.last_movement_at)}</td>
            <td className="px-3 py-2 text-right">
              {tab === "received" && (
                <button
                  onClick={() => onQC(r)}
                  className="h-7 px-2 text-xs rounded bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white font-semibold inline-flex items-center gap-1 mr-1"
                  data-testid={`qc-initiate-${r.raw_material_id}`}
                >
                  <ShieldCheck className="h-3 w-3" /> Initiate QC
                </button>
              )}
              <button
                onClick={() => onLedger(r)}
                className="h-7 px-2 text-xs rounded bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium border border-gray-200 inline-flex items-center gap-1"
                data-testid={`qc-ledger-${r.raw_material_id}`}
              >
                <ScrollText className="h-3 w-3" /> Ledger
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QCHistoryTable({ rows, loading }) {
  const [stickerMap, setStickerMap] = useState({});
  const [selected, setSelected] = useState({});     // { qc_id: true }
  useEffect(() => {
    const ids = rows.map((r) => r.id).filter(Boolean);
    if (!ids.length) { setStickerMap({}); return; }
    api.get("/production/sticker-prints/summary", { params: { ref_type: "qc_event", ref_ids: ids.join(",") } })
      .then((r) => setStickerMap(r.data || {}))
      .catch(() => setStickerMap({}));
  }, [rows]);

  const openPrint = (r) => {
    const params = new URLSearchParams({
      product_id: r.product_id,
      qty: String(r.passed_qty),
      ref_type: "qc_event",
      ref_id: r.id,
      ref_no: r.qc_no,
    });
    window.open(`/production/sticker-print?${params.toString()}`, "_blank", "noopener");
  };

  const openBulkPrint = () => {
    const chosen = rows.filter((r) => selected[r.id] && r.passed_qty > 0 && r.product_id);
    if (!chosen.length) return;
    const items = chosen.map((r) => ({
      product_id: r.product_id, qty: r.passed_qty,
      ref_type: "qc_event", ref_id: r.id, ref_no: r.qc_no,
    }));
    const key = `bulk-${Date.now()}`;
    sessionStorage.setItem(key, JSON.stringify(items));
    window.open(`/production/sticker-print?bulk=${key}`, "_blank", "noopener");
  };

  const selectableIds = rows.filter((r) => r.passed_qty > 0 && r.product_id).map((r) => r.id);
  const selectedCount = selectableIds.filter((id) => selected[id]).length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(selectableIds.map((id) => [id, true])));
  };

  return (
    <>
      {selectedCount > 0 && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-center gap-3 sticky top-0 z-10" data-testid="qc-bulk-bar">
          <span className="text-xs text-red-900 font-semibold">{selectedCount} QC event(s) selected</span>
          <button onClick={openBulkPrint} className="ml-auto h-7 px-3 text-xs rounded bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white font-semibold inline-flex items-center gap-1" data-testid="qc-bulk-print">
            <Sticker className="h-3 w-3" /> Print stickers for selected
          </button>
          <button onClick={() => setSelected({})} className="text-[11px] text-red-700 underline">Clear</button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
            <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableIds.length === 0} data-testid="qc-select-all" /></th>
            <th className="px-3 py-2 w-32">QC #</th>
            <th className="px-3 py-2 w-40">Date / Time</th>
            <th className="px-3 py-2">Raw Material</th>
            <th className="px-3 py-2">Mapped Product</th>
            <th className="px-3 py-2 text-right w-20">Intake</th>
            <th className="px-3 py-2 text-right w-20">Passed</th>
            <th className="px-3 py-2 text-right w-20">Failed</th>
            <th className="px-3 py-2">Fail Reason</th>
            <th className="px-3 py-2">Warehouse</th>
            <th className="px-3 py-2">Unit</th>
            <th className="px-3 py-2 w-36">Stickers</th>
            <th className="px-3 py-2">QC By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading && <tr><td colSpan={13} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={13} className="px-3 py-10 text-center text-gray-400">No QC events yet.</td></tr>}
          {rows.map((r) => {
            const passed = Number(r.passed_qty || 0);
            const printed = Number(stickerMap[r.id]?.printed_qty || 0);
            const pending = Math.max(0, passed - printed);
            const canPrint = passed > 0 && r.product_id;
            return (
              <tr key={r.id} className="hover:bg-gray-50" data-testid={`qc-history-row-${r.id}`}>
                <td className="px-3 py-2">
                  {canPrint && (
                    <input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))} data-testid={`qc-select-${r.id}`} />
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{r.qc_no}</td>
                <td className="px-3 py-2 text-xs">{formatDateTime(r.created_at)}</td>
                <td className="px-3 py-2 text-xs">{r.raw_material_name}</td>
                <td className="px-3 py-2 text-xs">{r.product_name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs">{r.intake_qty}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-emerald-700">{r.passed_qty}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-red-700">{r.failed_qty}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.fail_reason || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.warehouse_name}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.production_unit_name || "—"}</td>
                <td className="px-3 py-2">
                  {canPrint ? (
                    <div className="flex items-center gap-1.5">
                      {printed === 0 && (
                        <span className="text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200">Pending</span>
                      )}
                      {printed > 0 && pending === 0 && (
                        <span className="text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">Done</span>
                      )}
                      {printed > 0 && pending > 0 && (
                        <span className="text-[10px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 bg-blue-50 text-blue-700 border-blue-200" title={`${printed}/${passed} printed`}>{printed}/{passed}</span>
                      )}
                      <button
                        onClick={() => openPrint(r)}
                        className="h-6 px-2 text-[11px] rounded bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white font-semibold inline-flex items-center gap-1"
                        data-testid={`qc-print-stickers-${r.id}`}
                        title={`Print ${pending || passed} sticker${(pending || passed) === 1 ? "" : "s"}`}
                      >
                        <Sticker className="h-3 w-3" /> Print
                      </button>
                    </div>
                  ) : <span className="text-[10px] text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">{r.qc_person_name}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// ---------------- Initiate QC Dialog ----------------

function InitiateQCDialog({ row, onClose, onDone }) {
  const user = getUser();
  const [warehouses, setWarehouses] = useState([]);
  const [productId, setProductId] = useState(null);
  const [productName, setProductName] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [warehouseId, setWarehouseId] = useState(null);
  const [intake, setIntake] = useState(String(row.qty || ""));
  const [passed, setPassed] = useState("");
  const [failed, setFailed] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { units: unitOptions, unitId, setUnitId, required: unitRequired } = useProductionUnit();

  // "Rg X" is the raw-material naming convention — the QC dropdown must
  // never surface such entries as retail SKUs.
  const isRgName = (n) => /^\s*Rg\s+/i.test(n || "");
  const userTouchedRef = React.useRef(false);

  // Preload warehouses + attempt to auto-map from RM→Product reverse lookup
  useEffect(() => {
    api.get("/production/warehouses").then((r) => {
      const active = (r.data || []).filter((w) => w.is_active !== false);
      setWarehouses(active);
      if (active.length === 1) setWarehouseId(active[0].id);
    });
    // Correct reverse lookup: which RETAIL PRODUCTS have this RM configured
    // as a raw material? (Set via Products → RM mapping in Settings.)
    // Returns [] for manually-added RMs.
    api.get(`/production/raw-materials/${row.raw_material_id}/manufactured-products`).then((r) => {
      if (userTouchedRef.current) return;
      const products = (r.data?.products || []).filter((p) => !isRgName(p.name));
      if (products.length === 1) {
        const p = products[0];
        setProductId(p.id);
        setProductName(p.name);
        setProductSearch(p.name);
        setProductOptions([{ id: p.id, name: p.name, sku: p.code || "" }]);
      } else if (products.length > 1) {
        // Multiple candidates — expose them all as the initial suggestion list
        // and let the operator pick.
        setProductSearch("");
        setProductOptions(products.map((p) => ({ id: p.id, name: p.name, sku: p.code || "" })));
        setShowDropdown(true);
      } else {
        // No mapping — leave the search box empty so the user can type.
        setProductSearch("");
      }
    }).catch(() => setProductSearch(""));
  }, [row.raw_material_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Typeahead — search for retail products matching the query, filtering out
  // any RG-prefixed rows (those are raw materials, not retail SKUs).
  useEffect(() => {
    const q = (productSearch || "").trim();
    if (!q) { setProductOptions([]); return; }
    const t = setTimeout(() => {
      api.get("/products", { params: { search: q, limit: 40, exclude_rg: true } })
        .then((r) => {
          const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          const opts = arr
            .filter((p) => !isRgName(p.name || p.product_name || ""))
            .map((p) => ({ id: p.id, name: p.name || p.product_name || "—", sku: p.sku || p.sku_code || "" }));
          setProductOptions(opts);
        })
        .catch(() => setProductOptions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [productSearch]);   // eslint-disable-line react-hooks/exhaustive-deps

  const pickProduct = (p) => {
    userTouchedRef.current = true;
    setProductId(p.id);
    setProductName(p.name);
    setProductSearch(p.name);
    setShowDropdown(false);
  };

  const intakeN = Number(intake) || 0;
  const passedN = Number(passed) || 0;
  const failedN = Number(failed) || 0;
  const totalAccounted = passedN + failedN;
  const untally = Math.abs(totalAccounted - intakeN) > 0.001;
  const overIntake = intakeN > (row.qty || 0);

  const submit = async () => {
    if (!productId) return toast.error("Pick the mapped retail product");
    if (!warehouseId) return toast.error("Select the destination warehouse");
    if (unitRequired && !unitId) return toast.error("Select a Production Unit");
    if (intakeN <= 0) return toast.error("Intake qty must be > 0");
    if (overIntake) return toast.error(`Intake cannot exceed available ${row.qty}`);
    if (untally) return toast.error("Passed + Failed must equal Intake");
    if (failedN > 0 && !reason.trim()) return toast.error("Enter a reason for the failed qty");
    try {
      setSaving(true);
      const r = await api.post("/production/qc-events", {
        raw_material_id: row.raw_material_id,
        product_id: productId,
        warehouse_id: warehouseId,
        intake_qty: intakeN,
        passed_qty: passedN,
        failed_qty: failedN,
        fail_reason: reason || null,
        notes: notes || null,
        production_unit_id: unitId || null,
      });
      toast.success(`QC completed — ${passedN} passed added to warehouse`);
      // Auto-open sticker print window when there are passed pieces
      if (passedN > 0 && r.data?.id) {
        const params = new URLSearchParams({
          product_id: productId,
          qty: String(passedN),
          ref_type: "qc_event",
          ref_id: r.data.id,
          ref_no: r.data.qc_no || "",
        });
        window.open(`/production/sticker-print?${params.toString()}`, "_blank", "noopener");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "QC failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="qc-dialog">
        <DialogHeader>
          <DialogTitle>Initiate Quality Control — {row.raw_material_name}</DialogTitle>
          <DialogDescription>
            Deduct finished stock, split into <b>Passed</b> and <b>Failed</b>, and credit the passed qty to the warehouse against the mapped retail product.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="QC Person">
            <Input value={user?.name || user?.email || "—"} disabled data-testid="qc-person" />
          </F>
          <F label="QC Date">
            <Input value={formatDate(new Date())} disabled />
          </F>
          <F label="Raw Material (Finished Item)">
            <Input value={`${row.raw_material_name} · Available ${row.qty} ${row.unit || ""}`} disabled data-testid="qc-rm" />
          </F>
          <F label="Mapped Retail Product *">
            <div className="relative">
              <Input
                value={productSearch}
                onChange={(e) => {
                  userTouchedRef.current = true;
                  setProductSearch(e.target.value);
                  setProductId(null);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder="Type to search retail product…"
                data-testid="qc-product"
                autoComplete="off"
              />
              {showDropdown && productOptions.length > 0 && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                  {productOptions.slice(0, 20).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickProduct(p); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-gray-100 last:border-0 ${productId === p.id ? "bg-emerald-50" : ""}`}
                      data-testid={`qc-product-opt-${p.id}`}
                    >
                      <div className="font-medium text-gray-900">{p.name}</div>
                      {p.sku && <div className="text-[10px] text-gray-500 font-mono">{p.sku}</div>}
                    </button>
                  ))}
                </div>
              )}
              {productId && (
                <div className="text-[10px] text-emerald-700 mt-1 font-semibold">
                  ✓ Linked to <b>{productName}</b>
                </div>
              )}
            </div>
          </F>
          <F label="Warehouse *">
            <select
              value={warehouseId || ""}
              onChange={(e) => setWarehouseId(e.target.value || null)}
              className="h-9 w-full rounded-md border border-gray-200 px-2 text-sm bg-white"
              data-testid="qc-warehouse"
            >
              <option value="">Select warehouse…</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ""}</option>
              ))}
            </select>
          </F>
          {unitRequired && (
            <F label="Production Unit *">
              <ComboBoxSearch value={unitId} onChange={setUnitId} options={unitOptions} placeholder="Select production unit" testId="qc-unit" />
            </F>
          )}
          <F label={`Intake Qty (max ${row.qty})`}>
            <Input type="number" step="0.001" min="0" max={row.qty} value={intake} onChange={(e) => setIntake(e.target.value)} className={overIntake ? "border-red-500" : ""} data-testid="qc-intake" />
          </F>
          <F label="Passed Qty *">
            <Input type="number" step="0.001" min="0" value={passed} onChange={(e) => setPassed(e.target.value)} className={untally ? "border-amber-400" : ""} data-testid="qc-passed" />
          </F>
          <F label="Failed Qty">
            <Input type="number" step="0.001" min="0" value={failed} onChange={(e) => setFailed(e.target.value)} className={untally ? "border-amber-400" : ""} data-testid="qc-failed" />
          </F>
          {failedN > 0 && (
            <F label="Failure Reason *" className="col-span-2">
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 px-2 text-sm" data-testid="qc-reason">
                <option value="">— pick a reason —</option>
                <option value="Damaged">Damaged</option>
                <option value="Poor Finish">Poor Finish</option>
                <option value="Colour Mismatch">Colour Mismatch</option>
                <option value="Bent / Broken">Bent / Broken</option>
                <option value="Other">Other</option>
              </select>
            </F>
          )}
          <F label="Notes (optional)" className="col-span-2">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qc-notes" />
          </F>
        </div>
        <div className={`text-xs rounded-md px-3 py-2 mt-1 ${untally ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
          {untally
            ? <>Passed {passedN} + Failed {failedN} = <b>{totalAccounted}</b> · must equal Intake <b>{intakeN}</b></>
            : <>Ready — <b>{passedN}</b> pieces will be added to the selected warehouse; <b>{failedN}</b> logged as failed{reason ? ` (${reason})` : ""}. {passedN > 0 && <>The <b>sticker-print window</b> opens automatically after Complete.</>}</>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5 text-white" onClick={submit} disabled={saving} data-testid="qc-submit">
            <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving…" : "Complete QC"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerDialog({ rm, status, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/production/finished-stocks/ledger", {
      params: { raw_material_id: rm.raw_material_id, status, page: 1, page_size: 200 },
    }).then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Ledger load failed"))
      .finally(() => setLoading(false));
  }, [rm, status]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="qc-ledger-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex items-center">
          <div>
            <h3 className="font-heading font-bold text-lg text-gray-900">{rm.raw_material_name}</h3>
            <p className="text-xs text-gray-500">{status.charAt(0).toUpperCase() + status.slice(1)} ledger · {rows.length} entries</p>
          </div>
          <button onClick={onClose} className="ml-auto h-8 w-8 rounded hover:bg-gray-100 inline-flex items-center justify-center">✕</button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-40">Date / Time</th>
                <th className="px-3 py-2 w-32">Ref #</th>
                <th className="px-3 py-2 text-right w-24">Qty</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Recorded By</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No entries.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 tabular-nums text-xs">{formatDateTime(r.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{r.ref_no}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.qty_delta < 0 ? "text-red-700" : ""}`}>{r.qty_delta}</td>
                  <td className="px-3 py-2 text-xs">{r.source || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.created_by_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function F({ label, className = "", children }) {
  return <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>;
}
